import { ChevronDown, ClipboardList } from 'lucide-react';
import AtriumLogo from './AtriumLogo';
import DeepResearchButton from './DeepResearchButton';
import CCTVMonitoringButton from './CCTVMonitoringButton';
import MonitorButtons from './MonitorButtons';
import SustainabilityButton from './SustainabilityButton';
import { IfAIEnabled } from '../contexts/AIVisibilityContext';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_resort-map-explorer/artifacts/unjb22qg_images%20%281%29.png";

export default function HeroSection({ settings, slides }) {
  const logoUrl = settings?.logo_url || DEFAULT_LOGO;
  // iter-130 — Canonical deck copy. We intentionally do NOT let CMS
  // settings override these three lines anymore: production DBs still
  // carry stale strings, which previously caused a "correct text →
  // wrong text" flicker as soon as /api/settings resolved. If the
  // copy ever needs to change again, edit it here.
  const heroTitle = 'Hotel Management Deck';
  const heroSubtitle = 'The Rebirth of Dreams Rose Hall\u00ae Resort & Spa';
  const primaryColor = settings?.primary_color || '#1F4FBE';
  const bgColor = settings?.background_color || '#F4F1EB';

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1752005993124-3898870b203a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB0cm9waWNhbCUyMHJlc29ydCUyMHBvb2wlMjByZW5vdmF0aW9ufGVufDB8fHx8MTc3NjExMzM5NXww&ixlib=rb-4.1.0&q=85')`,
        }}
      />
      <div className="hero-overlay absolute inset-0" />

      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        {/* Co-branded lockup: property logo tile + Atrium platform mark.
            Both sit on the same translucent card so visitors instantly see
            the property AND the platform powering this transformation. */}
        <div
          className="animate-fade-slide-up delay-100 inline-flex items-center gap-3 mb-6 p-3 pr-5 rounded-2xl"
          style={{ backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)' }}
          data-testid="hero-cobranded-logos"
        >
          <img src={logoUrl} alt="Dreams Rose Hall" data-testid="hero-logo" className="h-14 md:h-20 object-contain" />
          <span
            aria-hidden
            className="hidden sm:inline-block w-px h-12 md:h-16"
            style={{ backgroundColor: 'rgba(0,61,165,0.25)' }}
          />
          <a
            href="#atrium-promo"
            data-testid="hero-atrium-logo"
            className="hidden sm:inline-flex items-center transition-opacity hover:opacity-80"
            title="Powered by Atrium"
          >
            <AtriumLogo variant="inline" size={36} accent="#1F4FBE" text="#1A2E2E" />
          </a>
        </div>

        <h1
          data-testid="hero-title"
          className="animate-fade-slide-up delay-200 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-white mb-6"
          style={{ fontFamily: "'Cinzel', 'Cormorant Garamond', serif", fontWeight: 400 }}
        >
          {heroTitle}
        </h1>

        <p
          data-testid="hero-tagline"
          className="animate-fade-slide-up delay-250 text-2xl sm:text-3xl md:text-4xl text-white max-w-3xl mx-auto mb-5 leading-snug"
          style={{
            fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
            fontWeight: 400,
            fontStyle: 'italic',
            letterSpacing: '0.005em',
          }}
        >
          A comprehensive tool for Asset Managers, VPs, ownership, and multi-property leaders to manage and control the entire hotel operation from a single screen.
        </p>

        <p
          data-testid="hero-subtitle"
          className="animate-fade-slide-up delay-300 text-base md:text-lg text-[#EAE5D9] max-w-2xl mx-auto mb-10 leading-relaxed"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {heroSubtitle}
        </p>

        <div className="animate-fade-slide-up delay-400 inline-flex flex-col sm:flex-row items-center gap-3 justify-center">
          <a
            href="#map-section"
            data-testid="hero-cta"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: primaryColor, border: '1px solid rgba(163, 212, 212, 0.3)' }}
            onClick={(e) => {
              e.preventDefault();
              // Open the project-wide search dialog so visitors can jump
              // straight to any widget by name / status / assignee.
              try { window.dispatchEvent(new CustomEvent('atrium:open-project-search')); } catch (err) { /* no-op */ }
            }}
          >
            Tour the Transformation
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </a>

          {/* iter-118 — Project Management · PACER glowing CTA (gold/amber). */}
          <button
            type="button"
            data-testid="hero-tasks-cta"
            onClick={() => {
              const authed = typeof window !== 'undefined' && !!localStorage.getItem('admin_token');
              if (!authed) {
                if (window.confirm('Project Management · PACER is for signed-in users. Continue to sign in?')) {
                  window.location.href = '/admin';
                }
                return;
              }
              window.location.href = '/pm';
            }}
            className="pm-tasks-glow inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-bold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5"
            style={{
              fontFamily: 'Manrope, sans-serif',
              background: 'linear-gradient(135deg, #B8860B 0%, #D4AF37 35%, #F5C842 70%, #C8A258 100%)',
              border: '1px solid rgba(255, 215, 100, 0.55)',
              textShadow: '0 1px 2px rgba(0,0,0,0.35)',
            }}
          >
            <ClipboardList className="w-4 h-4" />
            Project Management · PACER
          </button>
        </div>

        {/* iter-80 — AI Deep Research CTA, sits directly below the
            Tour the Transformation pill. Same size + glow that screams
            "click me" so visitors notice the AI angle without it
            stealing the primary CTA's attention. Wrapper handles the
            fade-in (the button's own animation slot is taken by the
            glow keyframes). Hidden when the engineer AI kill-switch
            is engaged. */}
        <div className="animate-fade-slide-up delay-500 mt-4 flex flex-wrap items-center justify-center gap-3">
          <IfAIEnabled>
            <DeepResearchButton />
          </IfAIEnabled>
          <CCTVMonitoringButton />
        </div>

        {/* Live monitors sit right beneath the AI Deep Research / CCTV
            CTA row per user request — same tight spacing so they read as
            one button cluster near the top of the page. */}
        <MonitorButtons slides={slides} settings={settings} />

        {/* Sustainability Projects & Initiatives — directly below the
            monitors in the same top cluster (coming-soon panel for now). */}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
          <SustainabilityButton />
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: `linear-gradient(to top, ${bgColor}, transparent)` }} />
    </section>
  );
}
